import React, { useState, useEffect } from 'react';
import Header from '../../Header';
import './StripeAdmin.scss';

const StripeAdmin = () => {
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totals, setTotals] = useState({
        monthlyTotal: 0,
        annualTotal: 0,
        activeSubscriptions: 0
    });

    useEffect(() => {
        const fetchSubscriptions = async () => {
            try {
                const token = localStorage.getItem('auth_token');
                if (!token) {
                    throw new Error('Authentication token not found');
                }

                const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/get_subscriptions.php`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch subscriptions');
                }

                const data = await response.json();
                if (!data.success) {
                    throw new Error(data.message || 'Failed to fetch subscriptions');
                }

                // Format the subscription data
                const formattedSubscriptions = data.subscriptions.map(sub => ({
                    ...sub,
                    amount: parseFloat(sub.amount), // Convert string amount to number
                    amount_formatted: `£${parseFloat(sub.amount).toFixed(2)}` // Format amount properly
                }));

                setSubscriptions(formattedSubscriptions);
                
                // Calculate totals using the numeric amounts
                const activeSubs = formattedSubscriptions.filter(sub => sub.status === 'active');
                const monthlyAmount = activeSubs
                    .filter(sub => sub.subscription_type === 'monthly')
                    .reduce((total, sub) => total + sub.amount, 0);
                const annualAmount = activeSubs
                    .filter(sub => sub.subscription_type === 'yearly')
                    .reduce((total, sub) => total + sub.amount, 0);

                setTotals({
                    monthlyTotal: monthlyAmount,
                    annualTotal: annualAmount,
                    activeSubscriptions: activeSubs.length
                });

            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchSubscriptions();
    }, []);

    if (loading) {
        return (
            <div className="StripeAdmin container">
                <Header />
                <div className="loading">Loading subscriptions...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="StripeAdmin container">
                <Header />
                <div className="error">{error}</div>
            </div>
        );
    }

    return (
        <div className="StripeAdmin container">
            <Header />
            <h1>Stripe Subscriptions</h1>
            
            <div className="summary-section">
                <div className="totals">
                    <div className="total-item">
                        <div className="label">Monthly Revenue</div>
                        <div className="value">£{totals.monthlyTotal.toFixed(2)}</div>
                    </div>
                    <div className="total-item">
                        <div className="label">Annual Revenue</div>
                        <div className="value">£{totals.annualTotal.toFixed(2)}</div>
                    </div>
                    <div className="total-item">
                        <div className="label">Active Subscriptions</div>
                        <div className="value">{totals.activeSubscriptions}</div>
                    </div>
                </div>
            </div>

            <h2>Subscription List</h2>
            <table>
                <thead>
                    <tr>
                        <th>Player</th>
                        <th>Team</th>
                        <th>Status</th>
                        <th>Amount</th>
                        <th>Type</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                    </tr>
                </thead>
                <tbody>
                    {subscriptions.map((subscription) => (
                        <tr key={subscription.id}>
                            <td className="player-cell">
                                <div className="player-name">{subscription.player_name}</div>
                                <div className="player-email">{subscription.email}</div>
                            </td>
                            <td className="team-cell">
                                <div className="team-name">{subscription.team_name}</div>
                                <div className="team-type">{subscription.team_type}</div>
                            </td>
                            <td className="status-cell">
                                <span className={`status ${subscription.status}`}>
                                    {subscription.status}
                                </span>
                            </td>
                            <td className="amount-cell">{subscription.amount_formatted}</td>
                            <td>{subscription.subscription_type}</td>
                            <td className="date-cell">{subscription.start_date}</td>
                            <td className="date-cell">{subscription.end_date || '-'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default StripeAdmin; 